import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import { ApiError } from '@/lib/api/client';
import type { IqServiceInventoryItem } from '@/types/iq-service-inventory';
import {
  IqServiceInventoryFields,
  emptyUpsertValues,
  isUpsertFormValid,
  upsertValuesFromItem,
  type IqServiceUpsertValues,
} from './iq-service-inventory-fields';

export type { IqServiceUpsertValues };

interface IqServiceInventoryUpsertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** Prefill source in edit mode (carries the optimistic-lock updatedAt). */
  initial?: IqServiceInventoryItem | null;
  /**
   * Persist the values. Rejects with ApiError → the dialog shows an inline error and
   * stays open. Resolves `false` → the caller handled it (e.g. 409 reload) and the
   * dialog stays open (re-prefilled). Resolves otherwise → success, the dialog closes.
   */
  onSubmit: (values: IqServiceUpsertValues) => Promise<boolean | void>;
}

/** Register (UC-2) and edit + status transition (UC-3) in one dialog. */
export function IqServiceInventoryUpsertDialog({
  open,
  onOpenChange,
  mode,
  initial,
  onSubmit,
}: IqServiceInventoryUpsertDialogProps) {
  const { t } = useTranslation('iq-service-inventory');

  const [values, setValues] = useState<IqServiceUpsertValues>(emptyUpsertValues);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const patch = (p: Partial<IqServiceUpsertValues>) => setValues((v) => ({ ...v, ...p }));

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setPending(false);
    setValues(mode === 'edit' && initial ? upsertValuesFromItem(initial) : emptyUpsertValues());
  }, [open, mode, initial]);

  const canSubmit = isUpsertFormValid(values, mode) && !pending;

  const resolveError = (err: unknown): string => {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        return mode === 'create' ? t('upsert.error.duplicate') : t('upsert.error.staleLock');
      }
      if (err.status === 422) return t('upsert.error.invalidTransition');
      if (err.status === 400) return err.message || t('upsert.error.validation');
    }
    return err instanceof Error && err.message ? err.message : t('upsert.error.generic');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);
    setPending(true);
    try {
      const result = await onSubmit({
        ...values,
        iqServiceKey: values.iqServiceKey.trim(),
        displayName: values.displayName.trim(),
        description: values.description.trim(),
        internetqEmailRef: values.internetqEmailRef.trim(),
      });
      // `false` = caller handled it but wants the dialog kept open (e.g. 409 reload).
      if (result !== false) onOpenChange(false);
    } catch (err) {
      setSubmitError(resolveError(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t(mode === 'create' ? 'upsert.createTitle' : 'upsert.editTitle')}</DialogTitle>
          <DialogDescription>{t('upsert.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <IqServiceInventoryFields
            values={values}
            onChange={patch}
            mode={mode}
            initialStatus={initial?.status}
          />

          {submitError && (
            <p role="alert" className="text-sm text-destructive">
              {submitError}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              {t('upsert.cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t(mode === 'create' ? 'upsert.submitCreate' : 'upsert.submitEdit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
